import React, { FC, useEffect, useMemo, useState } from 'react';
import { FormRenderProps } from 'react-final-form';
import { InputField } from '@features/form';
import { Button, Select } from '@shared/UI';
import './AssignPositionForm.scss';
import CloseSingleSecurityDrawerStore from '@widgets/OrderTable/store/account-refresh-store';
import { getAllAccounts, getAllOrders, getAllSecurities } from '@widgets/OrderTable/api';
import { error } from 'console';

interface IAssignBulkPositionForm extends FormRenderProps<any> {
    loading: boolean;
    order: any;
}

export const AssignBulkPositionForm: FC<IAssignBulkPositionForm> = (props) => {
    const { loading, handleSubmit } = props;

    const setIsDrawerClosed = CloseSingleSecurityDrawerStore((state) => state.setIsDrawerClosed);

    const [securities, setSecurities] = useState<any[]>([]);
    const [orders, setOrders] = useState<any[]>([]);
    const [accounts, setAccounts] = useState<any[]>([]);
    const [updatedOrders, setUpdatedOrders] = useState<any[]>([]);
    const [selectedOrders, setSelectedOrders] = useState<number[]>([]);
    const [submissionError, setSubmissionError] = useState<string | null>(null); // State for submission error

    const selectedOrderDetails = useMemo(() => {
        return updatedOrders?.filter((order: any) => selectedOrders.includes(order.id)) || [];
    }, [selectedOrders, updatedOrders]);

    const averagePricePerUnit = useMemo(() => {
        const totalQuantity = selectedOrderDetails.reduce((acc, order) => acc + (order.remainingQuantity || 0), 0);

        if (totalQuantity === 0) return 0; // Avoid dividing by zero

        const totalWeightedPrice = selectedOrderDetails.reduce(
            (acc, order) => acc + (order.updatedPrice || 0) * (order.remainingQuantity || 0),
            0,
        );

        return totalWeightedPrice / totalQuantity; // Weighted average
    }, [selectedOrderDetails]);

    const totalQuantity = useMemo(
        () => selectedOrderDetails.reduce((acc, order) => acc + (order.remainingQuantity || 0), 0),
        [selectedOrderDetails],
    );

    useEffect(() => {
        props.form.change('quantity', totalQuantity);
        props.form.change('price', averagePricePerUnit);
    }, [totalQuantity, averagePricePerUnit, props.form]);

    const userStorage = localStorage.getItem('User');
    const userObject = userStorage ? JSON.parse(userStorage) : null;

    useEffect(() => {
        const fetchData = async () => {
            try {
                const fetchedSecurities = await getAllSecurities(userObject ? userObject?.accounts[0]?.id : 0);
                const fetchedOrders = await getAllOrders(userObject ? userObject?.accounts[0]?.id : 0);
                setSecurities(fetchedSecurities);

                if (userObject) {
                    setAccounts(userObject?.subAccounts);
                } else {
                    const accounts = await getAllAccounts();
                    setAccounts(accounts);
                }

                setOrders(fetchedOrders);
                setUpdatedOrders(fetchedOrders);
            } catch (error) {}
        };

        fetchData();
    }, []);

    const validateError = () => {
        if (!props.values.securityId || !props.values.orderId || !props.values.accountId || totalQuantity === 0) {
            if (!props.values.securityId) {
                setSubmissionError('Please select Security before submitting.');
            } else {
                if (totalQuantity === 0) {
                    setSubmissionError('Please select Orders before submitting.');
                } else {
                    if (!props.values.accountId) {
                        setSubmissionError('Please select Account before submitting.');
                    }
                }
            }
        } else {
            setSubmissionError(null); // Clear any previous error
        }
    };

    const handleFormSubmit = (event: React.FormEvent) => {
        event.preventDefault(); // Prevent default form submission

        // Check if required fields are selected
        if (!props.values.securityId || !props.values.orderId || !props.values.accountId || totalQuantity === 0) {
            if (!props.values.securityId) {
                setSubmissionError('Please select Security before submitting.');
            } else {
                if (totalQuantity === 0) {
                    setSubmissionError('Please select Orders before submitting.');
                } else {
                    if (!props.values.accountId) {
                        setSubmissionError('Please select Account before submitting.');
                    }
                }
            }
            return; // Stop form submission
        }

        setSubmissionError(null); // Clear any previous error
        handleSubmit(event); // Proceed with form submission
    };

    return (
        <form
            role='form'
            onSubmit={handleFormSubmit} // Use custom submit handler
            className='assign-position-lot-form'
        >
            {/* Display submission error at the top of the form */}
            {submissionError && <div className='submission-error-message'>{submissionError}</div>}

            <div className='assign-position-lot-form__inputs'>
                <div className='text'>Security</div>
                <Select
                    allowClear
                    currentValue={props.values.securityId?.toString()}
                    options={
                        securities?.map((security: any) => ({
                            value: security.id,
                            label: security.symbol,
                        })) || []
                    }
                    placeholder='Select Security'
                    onSelect={(value) => {
                        props.form.change('securityId', value);
                        let filtered;
                        if (value) {
                            filtered = orders.filter((order) => order.securityId === value);
                            setUpdatedOrders(filtered);
                        } else {
                            setUpdatedOrders(orders);
                        }
                        if (props.values.orderType) {
                            if (props.values.orderType === 'buy') {
                                // Show orders where quantity is positive
                                filtered = orders.filter((order) => order.quantity > 0);
                            } else if (props.values.orderType === 'sell') {
                                // Show orders where quantity is negative
                                filtered = orders.filter((order) => order.quantity < 0);
                            }
                            setUpdatedOrders(filtered ? filtered : []);
                        }
                        if (submissionError) {
                            validateError();
                        }
                    }}
                    className='wide-field'
                />
                <div className='text'>Orders Type</div>
                <Select
                    allowClear
                    currentValue={props.values.orderType?.toString()}
                    options={[
                        { value: 'buy', label: 'Buy' },
                        { value: 'sell', label: 'Sell' },
                    ]}
                    placeholder='Select Order Type'
                    onSelect={(value) => {
                        props.form.change('orderType', value);

                        let filteredOrders = orders;

                        // Filter orders based on the selected order type
                        if (value === 'buy') {
                            // Show orders where quantity is positive
                            filteredOrders = orders.filter((order) => order.quantity > 0);
                        } else if (value === 'sell') {
                            // Show orders where quantity is negative
                            filteredOrders = orders.filter((order) => order.quantity < 0);
                        }

                        if (props.values.securityId) {
                            filteredOrders = filteredOrders.filter(
                                (order) => order.securityId === props.values.securityId,
                            );
                        }

                        setUpdatedOrders((prev) => {
                            if (JSON.stringify(prev) !== JSON.stringify(filteredOrders)) {
                                return filteredOrders;
                            }
                            return prev;
                        });

                        if (submissionError) {
                            validateError();
                        }
                    }}
                    className='wide-field'
                />
                <div className='text'>Orders</div>
                <Select
                    multiple
                    currentValue={props.values.orderId}
                    fromDropdown={'Order'}
                    options={
                        updatedOrders?.map((order: any) => {
                            const date = new Date(order.createDate);
                            const formattedDate = `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;

                            return {
                                value: order.id,
                                label: `${formattedDate} | Remaining Quantity: ${order.remainingQuantity} | Quantity: ${order.quantity}`,
                            };
                        }) || []
                    }
                    placeholder='Select Orders'
                    onSelect={(value) => {
                        props.form.change('orderId', value);
                        setSelectedOrders(value);
                        if (submissionError) {
                            validateError();
                        }
                    }}
                    className='wide-field'
                />
                <div className='text'>Account</div>
                <Select
                    allowClear
                    currentValue={props.values.accountId?.toString()}
                    options={
                        accounts.map((subAccount: any) => ({
                            value: subAccount.id,
                            label: `${subAccount.verificationRequest.firstName} ${subAccount.verificationRequest.lastName} - ${subAccount.fdid}`,
                        })) || []
                    }
                    placeholder='Select Account'
                    onSelect={(value) => {
                        props.form.change('accountId', value);
                        if (submissionError) {
                            validateError();
                        }
                    }}
                    className='wide-field'
                />
                <div className='text'>Total Quantity</div>
                <InputField
                    name='quantity'
                    placeholder={totalQuantity}
                    label='Quantity'
                    infoTooltip='Quantity'
                    type='number'
                    disabled
                />
                {props.errors?.quantity && props.touched?.quantity && (
                    <div className='error-message'>{props.errors.quantity}</div>
                )}
                <div className='text'>Average Price</div>
                <InputField
                    name='AveragePrice'
                    placeholder={'$' + averagePricePerUnit.toString()}
                    label='AveragePrice'
                    infoTooltip='Average Price'
                    type='number'
                    disabled
                />
                {props.errors?.AveragePrice && props.touched?.AveragePrice && (
                    <div className='error-message'>{props.errors.AveragePrice}</div>
                )}
            </div>
            <div style={{ display: 'flex' }}>
                <Button
                    loading={loading}
                    type='submit'
                >
                    Assign
                </Button>
                <Button
                    type='button'
                    onClick={() => setIsDrawerClosed(true)}
                    style={{ marginLeft: '8px', color: '#075985', background: '#EDEFF2' }}
                >
                    Cancel
                </Button>
            </div>
        </form>
    );
};
